import { type FC, useCallback } from "react";

import type { VatValueEntity } from "src/app/features/VatCode/types/VatValueEntity";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { TableAddNewLineButton } from "../../../Table/components/PropertyTable/TableAddNewLineButton";
import { Actions } from "../../../Table/types/types";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../types/types";
import { DatePickerInput } from "../DatePicker/DatePicker";
import { NumberInput } from "../Number/NumberInput";

export interface VatCodeVatValuesTableProps extends AntCustomFormField<VatValueEntity[]>, CommonFieldAttributes {}

export const VatCodeVatValuesTable: FC<VatCodeVatValuesTableProps> = ({ value = [], onChange, readOnly }) => {
    const appendRowId = (array: VatValueEntity[]) =>
        array
            .slice()
            .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
            .map((item, idx) => ({ ...item, rowId: idx }));

    const onInput = (cellId: string | number, key: keyof VatValueEntity, newValue: string | boolean) => {
        const newData = value.map((row) => {
            if (row.id !== cellId) return row;
            return { ...row, [key]: newValue };
        });
        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<VatValueEntity>[] => {
        return [
            {
                title: t("codeVat.vatValues.table.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("codeVat.vatValues.table.validFrom"),
                key: "validFrom",
                dataIndex: "validFrom",
                render: (_, record) => (
                    <DatePickerInput
                        name={`validFrom-${record.id}`}
                        value={record.validFrom}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "validFrom", newValue)}
                        format="YYYY-MM-DDTHH:mm"
                    />
                ),
            },
            {
                title: t("codeVat.vatValues.table.validTo"),
                key: "validTo",
                dataIndex: "validTo",
                render: (_, record) => (
                    <DatePickerInput
                        name={`validTo-${record.id}`}
                        value={record.validTo}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "validTo", newValue)}
                        format="YYYY-MM-DDTHH:mm"
                    />
                ),
            },
            {
                title: t("codeVat.vatValues.table.value"),
                key: "value",
                dataIndex: "value",
                render: (_, record) => (
                    <NumberInput
                        name={`value-${record.id}`}
                        label=""
                        value={record.value?.toString()}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "value", newValue)}
                    />
                ),
            },
        ];
    };

    const onDataChanged = useCallback(
        (data: VatValueEntity[]) => {
            if (onChange) {
                onChange(data);
            }
        },
        [onChange],
    );

    const addNewLine = () => {
        const emptyTableData = { id: undefined, validFrom: "", validTo: "", value: 0 } as VatValueEntity;
        const newTableValue = [...value, emptyTableData];
        if (onChange) {
            onChange(newTableValue);
        }
    };

    return (
        <PropertyTable<VatValueEntity>
            name="vatCodeValues_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="rowId"
            tableData={appendRowId(value) || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            actions={[Actions.DELETE]}
            customActions={[<TableAddNewLineButton key="vatValue_add-new-line" callback={addNewLine} />]}
        />
    );
};
